import React, { useState } from "react";
import { Box, Typography, Button } from "@mui/material";
import { colors, fonts, shadows, radii } from "../theme/tokens.ts";

interface QuizProps {
  onComplete: (answers: { [key: number]: number }) => void;
}

export interface Question {
  id: string;
  text: string;
  options: string[];
}

const otherQuestions: Question[] = [
  {
    id: "Q1",
    text: "¿Cómo prefieres trabajar en equipo?",
    options: [
      "Liderando y tomando decisiones importantes",
      "Colaborando de manera equilibrada con otros",
      "Apoyando y siguiendo las directrices del equipo",
    ],
  },
  {
    id: "Q2",
    text: "¿Qué tipo de tareas te motivan más?",
    options: [
      "Proyectos creativos e innovadores",
      "Tareas estructuradas y bien definidas",
      "Desafíos complejos que requieren análisis profundo",
    ],
  },
  {
    id: "Q3",
    text: "¿Cómo manejas la presión y los plazos ajustados?",
    options: [
      "Me adapto rápidamente y trabajo bien bajo presión",
      "Prefiero planificar con anticipación para evitar el estrés",
      "Necesito tiempo para procesar y organizar mis pensamientos",
    ],
  },
];

export const questions: Question[] = [
  {
    id: "Q1",
    text: "¿Cómo describo mi entorno familiar en la infancia?",
    options: [
      "Mayormente amoroso y contenedor",
      "Con tensiones o carencias afectivas, pero con momentos valiosos",
      "Frío, distante o marcado por maltrato",
    ],
  },
  {
    id: "Q2",
    text: "¿Cómo viví mi entorno social en la infancia/adolescencia?",
    options: [
      "Me sentí aceptado/a y con vínculos positivos",
      "Tuve momentos de aceptación y otros de rechazo o burlas",
      "Me sentí aislado/a o excluido/a la mayor parte del tiempo",
    ],
  },
  {
    id: "Q3",
    text: "¿Qué educación emocional recibí de mi familia?",
    options: [
      "Aprendí a expresar y comprender mis emociones",
      "Se hablaba poco de emociones o de forma confusa",
      "Las emociones eran negadas, descalificadas o ridiculizadas",
    ],
  },
  {
    id: "Q4",
    text: "¿Qué percepción tengo hoy del mundo y de las personas?",
    options: [
      "En general confío y veo lo positivo en los demás",
      "Depende de la situación, me cuesta confiar",
      "Tiendo a pensar que debo estar en guardia",
    ],
  },
  {
    id: "Q5",
    text: "¿Qué actitud tengo hoy frente a los demás?",
    options: [
      "Con quienes confío me abro, con los demás soy más reservado/a",
      "Me cuesta abrirme incluso en vínculos cercanos",
      "Evito el contacto emocional, aun en relaciones importantes",
    ],
  },
  {
    id: "Q6",
    text: "¿Qué tan claro tenés hoy tu rumbo personal?",
    options: [
      "Muy claro, tengo metas definidas",
      "Tengo ideas pero no siempre claridad",
      "Estoy perdido/a o sin rumbo",
    ],
  },
  {
    id: "Q7",
    text: "¿Cómo es tu estado emocional en general?",
    options: [
      "Estable, me autoobservo y me autorregulo bien",
      "Inestable, a veces me desbordo",
      "No tengo herramientas, me encierro en mi malestar",
    ],
  },
  {
    id: "Q8",
    text: "¿Qué hacés frente a las dificultades o en momentos difíciles?",
    options: [
      "Uso recursos internos y actúo con decisión",
      "Me apoyo en otros",
      "Rumio, me encierro en mi malestar o me paralizo",
    ],
  },
  {
    id: "Q9",
    text: "¿Cómo definirías tu calidad de pensamiento?",
    options: [
      "Enfocado/a, puedo priorizar",
      "A veces me pierdo en pensamientos confusos",
      "Rumio y me cuesta decidir",
    ],
  },
  {
    id: "Q10",
    text: "¿Qué sentís sobre tus vínculos actuales?",
    options: [
      "Nutritivos y de apoyo",
      "Algunos me drenan energía",
      "Me siento solo/a o incomprendido/a",
    ],
  },
  {
    id: "Q11",
    text: "¿Cómo manejo la comunicación en mi vida?",
    options: [
      "Soy claro/a y comprensivo/a con los demás",
      "Me cuesta expresar lo que necesito",
      "Discuto o reacciono con facilidad",
    ],
  },
  {
    id: "Q12",
    text: "¿Cómo tomo decisiones importantes?",
    options: [
      "Observo, me autoobservo y decido con firmeza",
      "Lo hago, pero con mucha duda o demora",
      "Me paralizo o postergo indefinidamente",
    ],
  },
  {
    id: "Q13",
    text: "¿Cómo vivo los desafíos o cambios importantes?",
    options: [
      "Me entusiasman, los veo como oportunidad",
      "Me generan ansiedad y necesito pensarlos mucho",
      "Me cuesta iniciar y salir de mi zona de confort",
    ],
  },
  {
    id: "Q14",
    text: "¿Qué papel suelo asumir en un grupo?",
    options: [
      "Líder o inspirador/a",
      "Mediador/a o colaborador/a",
      "Observador/a o retraído/a",
    ],
  },
  {
    id: "Q15",
    text: "¿Qué busco al actuar?",
    options: [
      "Sentido y trascendencia",
      "Seguridad, orden y coherencia",
      "Intensidad y emoción",
    ],
  },
];
//eslint-disable-next-line
const Quiz: React.FC<QuizProps> = ({ onComplete }) => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState<{ [key: string]: number }>({});

  const total = questions.length;
  const isLast = currentQuestion === total - 1;
  const currentId = questions[currentQuestion].id;
  const selected = answers[currentId];
  const progressPct = Math.round((currentQuestion / total) * 100);

  const handleAnswerChange = (questionId: string, answer: number) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: answer,
    }));
  };

  const handleNext = () => {
    if (selected === undefined) return;
    if (isLast) {
      onComplete(answers);
    } else {
      setCurrentQuestion((prev) => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion((prev) => prev - 1);
    }
  };

  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        padding: 0,
      }}
    >
      <Box sx={{ maxWidth: 640, width: "100%", display: "flex", flexDirection: "column", gap: "12px" }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography
            sx={{ fontFamily: fonts.heading, fontWeight: 700, fontSize: "13px", color: colors.teal }}
          >
            Pregunta {currentQuestion + 1} de {total}
          </Typography>
          <Typography sx={{ fontFamily: fonts.body, fontSize: "11px", color: colors.faint }}>
            {Object.keys(answers).length}/{total} respondidas
          </Typography>
        </Box>

        <Box
          sx={{
            width: "100%",
            height: "4px",
            borderRadius: `${radii.pill}px`,
            background: colors.borderLight,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              height: "100%",
              width: `${progressPct}%`,
              background: colors.coral,
              borderRadius: `${radii.pill}px`,
              transition: "width 0.3s ease",
            }}
          />
        </Box>

        <Box
          sx={{
            background: "#ffffff",
            borderRadius: radii.card,
            padding: { xs: "14px", md: "18px" },
            boxShadow: shadows.card,
            display: "flex",
            flexDirection: "column",
            gap: "14px",
          }}
        >
          <Typography
            sx={{
              fontFamily: fonts.heading,
              fontWeight: 700,
              fontSize: "15px",
              color: colors.teal,
              lineHeight: 1.4,
            }}
          >
            {questions[currentQuestion].text}
          </Typography>

          <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            {questions[currentQuestion].options.map((option, index) => {
              const active = selected === index;
              return (
                <Box
                  key={index}
                  role="radio"
                  aria-checked={active}
                  tabIndex={0}
                  onClick={() => handleAnswerChange(currentId, index)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handleAnswerChange(currentId, index);
                    }
                  }}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    padding: "10px 12px",
                    borderRadius: radii.option,
                    border: `1.5px solid ${active ? colors.coral : colors.borderLight}`,
                    background: active ? colors.selectedTint : "#ffffff",
                    cursor: "pointer",
                    transition: "all 0.15s ease",
                  }}
                >
                  <Box
                    sx={{
                      width: 15,
                      height: 15,
                      borderRadius: "50%",
                      border: `2px solid ${active ? colors.coral : colors.radioIdle}`,
                      flexShrink: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Box
                      sx={{
                        width: 7,
                        height: 7,
                        borderRadius: "50%",
                        background: active ? colors.coral : "transparent",
                      }}
                    />
                  </Box>
                  <Typography sx={{ fontFamily: fonts.body, fontSize: "12.5px", color: colors.body, lineHeight: 1.5 }}>
                    {option}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Button
            onClick={handlePrevious}
            disableRipple
            disabled={currentQuestion === 0}
            sx={{
              background: "#ffffff",
              color: colors.teal,
              border: `1.5px solid ${colors.borderInput}`,
              borderRadius: `${radii.pill}px`,
              padding: "9px 16px",
              fontFamily: fonts.heading,
              fontWeight: 600,
              fontSize: "12px",
              textTransform: "none",
              opacity: currentQuestion > 0 ? 1 : 0.35,
              "&.Mui-disabled": { color: colors.teal, opacity: 0.35 },
            }}
          >
            ← Anterior
          </Button>

          <Button
            onClick={handleNext}
            disableRipple
            disabled={selected === undefined}
            sx={{
              background: colors.coral,
              color: "#ffffff",
              borderRadius: `${radii.pill}px`,
              padding: "9px 18px",
              fontFamily: fonts.heading,
              fontWeight: 600,
              fontSize: "12px",
              textTransform: "none",
              opacity: selected === undefined ? 0.4 : 1,
              "&:hover": { background: colors.coralHover },
              "&.Mui-disabled": { color: "#ffffff", opacity: 0.4 },
            }}
          >
            {isLast ? "Ver resultados" : "Siguiente"} →
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default Quiz;
